// frontend/packages/business-console/src/services/ryzzecastService.js
//
// Client for RyzzeCast (content syndication): compose a post once and route it
// to your website and connected channels. Backend is /api/v2/ryzzecast (see
// backend/routes/ryzzecastRoutes.js). The tenant is attached automatically by
// the api client's x-business-id interceptor, so we never pass business/slug.

import { api } from '@upryzze/services';

// Cheap discovery: is RyzzeCast enabled for this business? Used to gate the
// page. Returns { enabled: false } for anyone whose tenant flag is off (or on
// any error), so the page can render a friendly "not available" state.
export async function getRyzzecastStatus() {
  try {
    const { data } = await api.get('/v2/ryzzecast/status');
    return data || { enabled: false };
  } catch {
    return { enabled: false };
  }
}

// The tenant's posts, newest first. Returns { items: [...] }.
export async function listPosts() {
  const { data } = await api.get('/v2/ryzzecast/posts');
  return Array.isArray(data?.items) ? data.items : [];
}

// Compose a new draft. `routing` is a { channel: { decision } } map; omit to
// take the server default (website selected). Returns the created post.
export async function createPost({ text, routing } = {}) {
  const { data } = await api.post('/v2/ryzzecast/posts', {
    text,
    ...(routing ? { routing } : {}),
  });
  return data;
}

// Update a post's text / routing / status. Returns the updated post.
export async function updatePost(id, patch = {}) {
  const { data } = await api.patch(
    `/v2/ryzzecast/posts/${encodeURIComponent(id)}`,
    patch
  );
  return data;
}

// Generate (or refresh) a post's channel variants without publishing. Used by
// the website preview: shows exactly the text a publish would put on the site.
// Returns { item, generated, ok }.
export async function retonePost(id, channels) {
  const { data } = await api.post(
    `/v2/ryzzecast/posts/${encodeURIComponent(id)}/retone`,
    channels ? { channels } : {}
  );
  return data;
}

// Publish a post's website variant as a live blog post (one-click: writes to
// the site's preview branch, then promotes to live). Returns
// { ok, reason?, slug?, item }. ok:false with a reason means it did not go live.
export async function publishPost(id) {
  const { data } = await api.post(
    `/v2/ryzzecast/posts/${encodeURIComponent(id)}/publish`
  );
  return data;
}

// ---- Facebook connect (harvest wave) --------------------------------------
// The /v2/fb/* API is gated on the fb_harvest feature; a 404 means the feature
// is off for this tenant, so we report it as not-enabled and hide the card.

// Returns { enabled, connected?, pageName?, webhookSubscribed? }.
export async function getFacebookStatus() {
  try {
    const { data } = await api.get('/v2/fb/status');
    return { enabled: true, ...(data || {}) };
  } catch (err) {
    if (err?.response?.status === 404) return { enabled: false };
    return { enabled: false };
  }
}

// Returns the Meta OAuth dialog URL to send the browser to.
export async function getFacebookConnectUrl() {
  const { data } = await api.get('/v2/fb/connect-url');
  return data?.url || null;
}

export async function disconnectFacebook() {
  const { data } = await api.delete('/v2/fb/connection');
  return data;
}

// Import the connected Page's recent posts as drafts (idempotent server-side;
// re-running never duplicates). Returns { imported, fetched }.
export async function syncFacebookPosts(limit) {
  const { data } = await api.post('/v2/fb/sync-posts', limit ? { limit } : {});
  return data;
}
